/* @flow */

import * as React from 'react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import { action, computed, observable, reaction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import type { RouterStore } from 'mobx-react-router';
import type { RouteComponentProps } from 'react-router-dom';
import fastDeepEqual from 'fast-deep-equal';
import Tab from '@material-ui/core/Tab';
import Switch from '@material-ui/core/Switch';

/*
 * Somehow import does not work on local development and it works on
 * build pipelines, using require() work both local and pipelines.
 */
const { klona } = require('klona/full');

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import type {
  DataStoreEntry,
  LoadDataOptions,
  LoadSummaryDataOptions,
  SummaryDataStoreEntry,
} from 'stores/ReportViewStore';
import ReportViewStore, { loadSummaryOptionsToFilterItems } from 'stores/ReportViewStore';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import type { SingularConsolidatedFilterItem } from 'components/PageSearch';
import PageSearch from 'components/PageSearch';
import PageFilters from 'components/PageFilters/PageFilters';
import Loading from 'components/Loading/Loading';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import ActionDrawerStore from 'stores/ActionDrawerStore';
import UserStore from 'stores/UserStore';
import UserCaaSStore from 'stores/UserCaaSStore';
import RegionStore from 'stores/RegionStore';
import UltimatePaginationMUI from 'components/UltimatePaginationMUI/UltimatePaginationMUI';
import { ElectroTabs } from 'components/ExtendedUiElements/customized';
import {
  AllocateAssetToProject,
  DeleteCustomerAccount,
  DeleteEmailDomain,
  DisableAccount,
  DisableAccountMulti,
  EnableAccount,
  EnableAccountMulti,
  EndProject,
  EndProjectMulti,
  ExtendProject,
  ExtendProjectMulti,
  LoginAsUser,
  ReassignAsset,
  ReassignProjectLead,
  RequestAssetOwnership,
  RequestOwnershipMulti,
  ScheduleAssetCalibration,
  ScheduleAssetCalMulti,
  SelfAuditMulti,
  ViewAsUser,
  CaaSDetails,
  CaaSDetailsMultiple,
  ChangeMultipleAssetsLocations,
} from 'views/ActionDrawer';
import PageTitle from 'components/PageTitle';
import { concatParamKeyValues } from 'utils/compare';

import type { ReportViewState } from './state';
import * as stateLib from './state';
import type {
  ApiType,
  AvailableFilter,
  ConsolidatedFilterItem,
  DataViewConfig,
  SelectionDetails,
  SortSpec,
  SupportedUriTokenNames,
  URLFilterItem,
  UserSearchPreference,
  ViewConfig,
} from './types';
import { SORT_DIRECTION_VALUES, UserFilterItem } from './types';

import DataTable from '../DataTable/DataTable';
import RegionSelect from '../RegionSelect';
import type { IndicatorState as RefreshIndicatorState } from '../RefreshIndicator';
import RefreshIndicator from '../RefreshIndicator';
import type { SummaryData } from '../ReportSummary';
import ReportSummary from '../ReportSummary';
import { Roles } from '../../../permissions/defs';

import styles from './ReportView.module.scss';
import electroSwitchStyles from '../../../forms/components/inputs/ElectroSwitch/ElectroSwitch.module.scss';

export type ReportViewInjectedProps = {
  routing: RouterStore,
  reportViewStore: ReportViewStore,
  actionDrawerStore: ActionDrawerStore,
  userStore: UserStore,
  userCaaSStore: UserCaaSStore,
  regionStore: RegionStore,
} & RouteComponentProps &
  TranslatorProps;

export type ReportViewStateValidateCallback = (
  reportViewState: ReportViewState
) => ReportViewState | true;

export type ViewConfigGetter = (
  props: ReportViewInjectedProps,
  state: ReportViewState,
  getters: {
    getSummaryViewInfo: () => {
      region: ?string,
      brandId: ?string,
      accountId: ?string,
    },
  }
) => ViewConfig;

type _LoadDataReactionDependencies = {
  options: LoadDataOptions,
  dataPathName?: string,
  filtersPathnameOverride?: string,
  disableFilters?: boolean,
};

const SEARCHVIEWCODES = {
  ASSETS: 'assets',
  CONTRACTS: 'contracts',
  ORDERS: 'orders',
};

const TYPEOFFILTERS = {
  OPEN: 'open',
  ERSTATUS: 'er_status',
  ACCOUNT: 'account',
};

export default function createReportView(
  viewConfig: ViewConfig | ViewConfigGetter,
  options?: {
    onPrevalidateReportViewState?: ReportViewStateValidateCallback,
    onPostvalidateReportViewState?: ReportViewStateValidateCallback,
  }
) {
  @translate()
  @inject(
    'routing',
    'reportViewStore',
    'actionDrawerStore',
    'userStore',
    'userCaaSStore',
    'regionStore'
  )
  @observer
  class ReportViewPageBase extends ComponentWithInjectedProps<{}, {}, ReportViewInjectedProps> {
    // Public Members
    @computed get viewConfig(): ViewConfig {
      if (typeof viewConfig === 'function') {
        return viewConfig(this.props, this.reportViewState, {
          getSummaryViewInfo: this.getSummaryViewInfo,
        });
      }

      return viewConfig;
    }

    @observable filterDrawerOpen = false;

    @observable selection: SelectionDetails = {
      selectedItems: [],
      selectAll: false,
    };

    @computed get reportViewState(): ReportViewState {
      return stateLib.decodeStateUri(this.props.routing.location.search);
    }

    @computed get sort(): ?SortSpec {
      return this.reportViewState.sort || this.viewConfig.defaultSort;
    }

    @computed get searchTerm(): string {
      return `${
        this.reportViewState.searchTerm
          ? this.reportViewState.searchTerm
          : this.selectedUserSearchPreference &&
            this.selectedUserSearchPreference.searchConfig.search.length
          ? `${this.selectedUserSearchPreference.searchConfig.search.join(' ')}`
          : ''
      }`;
    }

    @computed get userSearchPreferences(): UserSearchPreference[] {
      return (
        (this.viewConfig.userFilterNamespace &&
          this.props.reportViewStore.userSearchPreferences.get(
            this.viewConfig.userFilterNamespace
          )) ||
        []
      );
    }

    @computed get selectedUserSearchPreference(): ?UserSearchPreference {
      return this.reportViewState.userSearchPreference
        ? this.userSearchPreferences.find(
            userSearchPreference =>
              userSearchPreference.id === this.reportViewState.userSearchPreference
          )
        : undefined;
    }

    @computed get implicitFilterItems(): ConsolidatedFilterItem[] {
      const results: ConsolidatedFilterItem[] = [];
      if (this.viewConfig.defaultFilters) {
        results.push(
          ...this.viewConfig.defaultFilters.map(item => ({
            key: item.key,
            values: [...item.values],
            isDefault: true,
          }))
        );
      }
      if (this.selectedUserSearchPreference) {
        results.push(
          ...this.selectedUserSearchPreference.searchConfig.filters.map(item => ({
            key: item.key,
            values: [item.value],
            type: item.type,
            userFilterId: this.selectedUserSearchPreference.id,
          }))
        );
      }
      if (this.viewConfig.type === 'SINGLE_WITH_SUMMARY') {
        const tokenFilterItems = loadSummaryOptionsToFilterItems(this.summaryTokenValues);

        results.push(
          ...tokenFilterItems.map(item => ({
            key: item.key,
            values: [...item.values],
            isDefault: true,
          }))
        );
      }

      return results;
    }

    @computed get filters(): ConsolidatedFilterItem[] {
      const { t, i18n } = this.props;
      const items: {
        [string]: ConsolidatedFilterItem,
      } = {};

      if (this.reportViewState.filters) {
        for (const filter of this.reportViewState.filters) {
          let item: ConsolidatedFilterItem = items[filter.key];

          if (filter.value) {
            filter.values = [filter.value];
          }

          if (item) {
            if (filter.overrideImplicit) {
              item.values = [...filter.values];
              item.overridesImplicit = true;
            } else {
              item.values = Array.from(new Set([...item.values, ...filter.values]));
            }
          } else {
            item = {
              key: filter.key,
              values: [...filter.values],
            };

            if (filter.overrideImplicit) {
              item.overridesImplicit = true;
            }

            items[filter.key] = item;
          }

          if (typeof filter.fixed !== 'undefined') {
            // @flow-disable-next-line Flow bug
            item.fixed = filter.fixed;
          }
        }
      }

      for (const implicitFilter of this.implicitFilterItems) {
        let item: ConsolidatedFilterItem = items[implicitFilter.key];

        if (item) {
          if (!item.overridesImplicit) {
            item.values = Array.from(new Set([...implicitFilter.values, ...item.values]));
          }
        } else {
          items[implicitFilter.key] = implicitFilter;
        }
      }

      // @flow-disable-next-line Flow bug
      const results: ConsolidatedFilterItem[] = Object.values(items);
      const hydratedResults = [...results];
      if (this.lastReasonableViewData) {
        if (this.lastReasonableViewData.availableFilters) {
          for (const result of results) {
            const matchingAvailableFilter = this.lastReasonableViewData.availableFilters.find(
              availableFilter => availableFilter.key === result.key
            );

            if (matchingAvailableFilter) {
              result.type = result.type || matchingAvailableFilter.type;
              const filterGroupNameKey = `report_view.${this.viewConfig.codename.toLowerCase()}.filter_labels.${
                matchingAvailableFilter.key
              }`;
              result.name = i18n.exists(filterGroupNameKey)
                ? t(filterGroupNameKey)
                : matchingAvailableFilter.name;

              const groupedFilterValues = Object.values(
                matchingAvailableFilter.groupedFilters ? matchingAvailableFilter.groupedFilters : {}
              );
              const mergedGroupedFilterValues = [];

              groupedFilterValues.forEach(group => mergedGroupedFilterValues.push(...group));

              const allFilters = [...matchingAvailableFilter.filters, ...mergedGroupedFilterValues];

              if (result.type !== 'date') {
                const matchingFilterValues: string[] = [];
                const unmatchingFilterValues: string[] = [];

                for (const filterValue of result.values) {
                  const matchingFilterValue = allFilters.find(
                    matchingAvailableFilterItem => matchingAvailableFilterItem.value === filterValue
                  );

                  if (matchingFilterValue) {
                    matchingFilterValues.push(filterValue);
                    result.labels = {
                      ...result.labels,
                      [matchingFilterValue.value]: matchingFilterValue.isGroup
                        ? `${matchingFilterValue.name} - ${matchingFilterValue.label}`
                        : matchingFilterValue.label,
                    };
                  } else {
                    unmatchingFilterValues.push(filterValue);
                  }
                }

                if (unmatchingFilterValues.length) {
                  result.values = matchingFilterValues;

                  hydratedResults.push({
                    ...result,
                    values: unmatchingFilterValues,
                    doesNotMatchAvailableFilters: true,
                  });
                }

                delete result.doesNotMatchAvailableFilters;
              }
            } else if (result.type !== 'date') {
              result.doesNotMatchAvailableFilters = true;
            }
          }

          // Sort to align with available filter order
          const availableFilterIndeces = this.lastReasonableViewData.availableFilters.reduce(
            (_availableFilterIndeces, availableFilter, index) => ({
              ..._availableFilterIndeces,
              [availableFilter.key]: index,
            }),
            {}
          );
          hydratedResults.sort(
            (resultA, resultB) =>
              availableFilterIndeces[resultA.key] - availableFilterIndeces[resultB.key]
          );
        }

        if (this.lastReasonableViewData.currentSearch) {
          for (const hydratedResult of hydratedResults) {
            const currentSearchItem = this.lastReasonableViewData.currentSearch[hydratedResult.key];

            if (currentSearchItem) {
              for (const value of hydratedResult.values) {
                const matchingCurrentSearchItem = currentSearchItem.values.find(
                  currentSearchItem => currentSearchItem.id === value
                );
                const matchingCurrentSearchItemNotInFilters = currentSearchItem.values
                  .filter(
                    currentSearchItem =>
                      hydratedResult.doesNotMatchAvailableFilters && currentSearchItem.key === value
                  )
                  .map(item => ({ ...item, label: currentSearchItem.label }))[0];

                if (
                  matchingCurrentSearchItemNotInFilters &&
                  matchingCurrentSearchItemNotInFilters.label
                ) {
                  hydratedResult.labels = {
                    ...hydratedResult.labels,
                    [matchingCurrentSearchItemNotInFilters.value]:
                      matchingCurrentSearchItemNotInFilters.label,
                  };
                  hydratedResult.type = 'notfoundstring';
                  hydratedResult.name = matchingCurrentSearchItemNotInFilters.label;
                  hydratedResult.value = matchingCurrentSearchItemNotInFilters.value;
                }

                if (matchingCurrentSearchItem && matchingCurrentSearchItem.label) {
                  hydratedResult.labels = {
                    ...hydratedResult.labels,
                    [value]: matchingCurrentSearchItem.label,
                  };
                }
              }
            }
          }
        }
      }

      return hydratedResults;
    }

    @computed get currentViewConfig(): DataViewConfig {
      let currentViewConfig;

      switch (this.viewConfig.type) {
        case 'TABBED':
          currentViewConfig = this.viewConfig.views[
            this.reportViewState.tab || Object.keys(this.viewConfig.views)[0]
          ];
          break;

        case 'SINGLE':
        case 'SINGLE_WITH_SUMMARY':
        default:
          currentViewConfig = this.viewConfig.view;
      }

      // Update columns visibility based on applied filters
      // when the current view config provides this functionality.
      if (currentViewConfig.setColumnsVisibilityBasedOnFilters) {
        currentViewConfig.setColumnsVisibilityBasedOnFilters(
          currentViewConfig.columns,
          this.filters
        );
      }

      return currentViewConfig;
    }

    @computed get currentViewData(): ?DataStoreEntry {
      if (this._dataKey && this.props.reportViewStore.dataStore.has(this._dataKey)) {
        return this.props.reportViewStore.dataStore.get(this._dataKey);
      }

      return undefined;
    }

    @computed get summaryTokenValues(): { [x: SupportedUriTokenNames]: string } {
      const result: { [x: SupportedUriTokenNames]: string } = {};

      if (
        this.viewConfig.type === 'SINGLE_WITH_SUMMARY' &&
        this.viewConfig.summaryView.useUriTokens
      ) {
        const {
          match: { params },
        } = this.props;

        for (const uriToken of this.viewConfig.summaryView.useUriTokens) {
          switch (uriToken) {
            case 'brandId': {
              const matchingParamValue = params.brandId;

              if (matchingParamValue) {
                result.brandId = matchingParamValue;
              }
              break;
            }

            case 'accountId': {
              const matchingParamValue = params.accountId;

              if (matchingParamValue) {
                result.accountId = matchingParamValue;
              }
              break;
            }

            default:
          }
        }
      }

      return result;
    }

    @computed get currentRegion(): ?string {
      const regionFilterItem = this.filters.find(
        filterItem => filterItem.key === RegionStore.REGION_FILTER_KEY
      );

      if (regionFilterItem) {
        const [value] = regionFilterItem.values;

        return value === 'ALL' ? undefined : value;
      }

      return undefined;
    }

    getSummaryViewInfo = () => ({
      region: this.currentRegion,
      brandId: this.props.match.params.brandId,
      accountId: this.props.match.params.accountId,
    });

    @computed get loadSummaryDataOptions(): ?LoadSummaryDataOptions {
      const result: LoadSummaryDataOptions = {
        codename: this.viewConfig.codename,
        ...this.summaryTokenValues,
      };

      const { currentRegion } = this;

      if (currentRegion) {
        result.region = this.currentRegion;
      }

      return result;
    }

    @computed get currentViewSummaryData(): ?SummaryDataStoreEntry {
      if (this.viewConfig.type !== 'SINGLE_WITH_SUMMARY') {
        return;
      }

      if (
        this._summaryDataKey &&
        this.props.reportViewStore.summaryDataStore.has(this._summaryDataKey)
      ) {
        return this.props.reportViewStore.summaryDataStore.get(this._summaryDataKey);
      }
    }

    @computed get currentViewSummaryLayout(): SummaryData {
      if (
        this.viewConfig.summaryView &&
        this.viewConfig.summaryView.mapDataToLayout &&
        this.currentViewSummaryData &&
        this.currentViewSummaryData.data
      ) {
        return this.viewConfig.summaryView.mapDataToLayout(this.currentViewSummaryData.data);
      }

      return [[]];
    }

    @computed get currentViewAvailableFilters(): AvailableFilter[] {
      if (this.currentViewData) {
        return this.currentViewData.availableFilters || [];
      }

      return [];
    }

    @computed get lastReasonableViewData(): ?DataStoreEntry {
      if (
        this._lastReasonableDataKey &&
        this.props.reportViewStore.dataStore.has(this._lastReasonableDataKey)
      ) {
        return this.props.reportViewStore.dataStore.get(this._lastReasonableDataKey);
      }
    }

    @computed get pageTotalCount(): number {
      if (this.lastReasonableViewData && this.lastReasonableViewData.meta) {
        if (
          this.lastReasonableViewData.meta.tabCounts &&
          typeof this.lastReasonableViewData.meta.tabCounts.total === 'number'
        ) {
          return this.lastReasonableViewData.meta.tabCounts.total;
        }

        return this.lastReasonableViewData.meta.totalResults || 0;
      }

      return 0;
    }

    @computed get refreshState(): RefreshIndicatorState {
      if (this.currentViewData) {
        if (this.currentViewData.firstLoad) {
          return 'loading';
        }

        if (this.currentViewData.loading) {
          return 'refreshing';
        }

        if (this.currentViewData.filtersLoading) {
          return 'loading-filters';
        }
      }

      return 'up-to-date';
    }

    @action
    updateReportViewState = (type: 'replace' | 'push', newState: ReportViewState) => {
      this.props.routing[type](
        stateLib.formulateUri(this.props.routing.location.pathname, newState)
      );
    };

    // TODO: Update `newState` to be partial `ReportViewState` when TS arrives
    @action
    patchReportViewState = (type: 'replace' | 'push', newState: ReportViewState) =>
      this.updateReportViewState(type, {
        ...this.reportViewState,
        ...newState,
      });

    @computed get loadDataOptions(): LoadDataOptions {
      let filters = this.filters.filter(filter => filter.values.length);

      if (
        this.viewConfig.summaryView &&
        this.viewConfig.summaryView.excludeFilterKeysFromReportView
      ) {
        filters = filters.filter(
          filter =>
            !this.viewConfig.summaryView.excludeFilterKeysFromReportView.includes(filter.key)
        );
      }

      return {
        codename: this.viewConfig.codename,
        page: this.reportViewState.page,
        filters,
        search: this.searchTerm,
        ...(this.sort ? { sort: this.sort } : {}),
      };
    }

    @action
    loadData = () => {
      const dataKey = this.props.reportViewStore.loadData(
        this.currentViewConfig,
        this.loadDataOptions,
        {
          filtersPathnameOverride: this.currentViewConfig.filtersPathnameOverride,
          disableFilters: this.viewConfig.disableFilters,
        }
      );

      this._dataKey = dataKey;
    };

    @observable _dataKey: ?string;
    @observable _lastReasonableDataKey: ?string;

    @action
    loadSummaryData = () => {
      if (this.viewConfig.summaryView && this.loadSummaryDataOptions) {
        const dataKey = this.props.reportViewStore.loadSummaryData(
          this.viewConfig.summaryView.dataPathname,
          this.loadSummaryDataOptions
        );

        this._summaryDataKey = dataKey;
      }
    };

    @observable _summaryDataKey: ?string;

    /**
     * Returns true if the filter "is_caas_asset" is
     * within the list if available filters.
     */
    @computed get isCaaSFilterAvailable() {
      return (
        this.props.userCaaSStore.hasCaaSAssets &&
        this.currentViewAvailableFilters.some(filter => filter.key === 'is_caas_asset')
      );
    }

    /**
     * Returns true if the filter "is_caas_asset" is active, whether it is activated
     * from the filters bar or the toggle button.
     */
    @computed get isCaaSFilterActive() {
      return this.filters.some(
        filter => filter.key === 'is_caas_asset' && filter.values.includes('Yes')
      );
    }

    constructor(props: ReportViewInjectedProps) {
      super(props);

      this._disposers.push(
        reaction(
          () => this.props.reportViewStore.shouldInvalidateCache,
          action(shouldInvalidateCache => {
            if (!shouldInvalidateCache) {
              return;
            }

            this.props.reportViewStore.invalidateCacheByCodename(this.viewConfig.codename);
            this.props.reportViewStore.shouldInvalidateCache = false;
          })
        )
      );

      if (!this.viewConfig) {
        throw new Error('Report Views require `viewConfig` to be defined');
      }

      this.props.userCaaSStore.checkForCaaSAssets(this.props.userStore.activeUser.id);
    }

    @computed get _loadDataReactionDependencies(): _LoadDataReactionDependencies {
      return {
        options: toJS(this.loadDataOptions),
        dataPathName: this.currentViewConfig.dataPathname,
        filtersPathnameOverride: this.currentViewConfig.filtersPathnameOverride,
        disableFilters: this.viewConfig.disableFilters,
      };
    }

    componentDidMount() {
      this._disposers.push(
        reaction(() => toJS(this._loadDataReactionDependencies), this._reportViewStateReaction, {
          fireImmediately: true,
        }),
        reaction(() => toJS(this.currentViewData), this._postValidateReportViewState),
        reaction(() => toJS(this.currentViewData), this._lastReasonableDataKeyReaction, {
          fireImmediately: true,
        }),
        reaction(() => toJS(this.loadSummaryDataOptions), this._summaryDataOptionsReaction, {
          fireImmediately: true,
        }),
        reaction(() => toJS(this.selectedUserSearchPreference), this._userSearchPreferencesReaction)
      );

      if (this.viewConfig.userFilterNamespace && !this.viewConfig.disableUserPreferenceSave) {
        this.props.reportViewStore.loadUserSearchPreference(this.viewConfig.userFilterNamespace);
      }
    }

    componentWillUnmount() {
      for (const disposer of this._disposers) {
        disposer();
      }
    }

    _lastReasonableDataKeyReaction = () => {
      if (
        !this._lastReasonableDataKey ||
        (this.currentViewData && (!this.currentViewData.loading || !this.currentViewData.firstLoad))
      ) {
        this._lastReasonableDataKey = this._dataKey;
      }
    };

    _disposers: Function[] = [];
    _currentReportViewDiffDetails: _LoadDataReactionDependencies;
    _defaultsEffected = false;

    _effectDefaults = (reportViewState: ReportViewState): ReportViewState => {
      this._defaultsEffected = true;
      const newReportViewState = { ...reportViewState };

      if (this.viewConfig.type === 'TABBED' && !reportViewState.tab) {
        newReportViewState.tab =
          this.viewConfig.defaultTab || Object.keys(this.viewConfig.views)[0];
      }

      if (
        this.viewConfig.supportedLayouts &&
        this.viewConfig.supportedLayouts.length > 1 &&
        this.viewConfig.defaultLayout &&
        !this.reportViewState.layout
      ) {
        newReportViewState.layout = this.viewConfig.defaultLayout;
      }

      if (this.viewConfig.type === 'SINGLE_WITH_SUMMARY' && !reportViewState.summary) {
        newReportViewState.summary = {
          expanded: true,
        };
      }

      return newReportViewState;
    };

    _userSearchPreferencesReaction = () => {
      const { selectedUserSearchPreference } = this;
      if (this.selectedUserSearchPreference) {
        const filters = selectedUserSearchPreference?.searchConfig.filters;
        const search = selectedUserSearchPreference?.searchConfig.search;

        this.updateReportViewState('replace', {
          ...this.reportViewState,
          filters: filters || undefined,
          searchTerm: search,
        });
      } else {
        this.updateReportViewState('replace', {
          ...this.reportViewState,
          filters: undefined,
          searchTerm: undefined,
        });
      }
    };

    _reportViewStateReaction = () => {
      if (this.props.routing.location.pathname !== this.props.location.pathname) {
        // We've navigated away
        return;
      }

      // Prevent format differences and effect default options
      if (
        stateLib.encodeStateUri(this.reportViewState) !==
          this.props.routing.location.search.replace('?', '') ||
        !this._defaultsEffected
      ) {
        let newState = this.reportViewState;

        newState = this._effectDefaults(newState);

        this.updateReportViewState('replace', newState);
      }

      // Prevalidation
      if (!this._prevalidateReportViewState()) {
        return;
      }

      // Data Query
      const jsReportViewDiffDetails = {
        ...klona(toJS(this._loadDataReactionDependencies)),
      };

      if (this._loadDataReactionDependencies.options.filters) {
        jsReportViewDiffDetails.options.filters = Object.values(
          this._loadDataReactionDependencies.options.filters.reduce(
            (_byKey, item) => ({
              ..._byKey,
              [item.key]: {
                key: item.key,
                values: Array.from(
                  new Set([
                    ...((_byKey[item.key] && _byKey[item.key].values) || []),
                    ...item.values,
                  ])
                ),
              },
            }),
            {}
          )
        )
          .map(filter => ({
            key: filter.key,
            values: [...filter.values],
          }))
          .sort((filterA, filterB) =>
            concatParamKeyValues(filterA).localeCompare(concatParamKeyValues(filterB))
          );
      }

      if (
        !this._currentReportViewDiffDetails ||
        !fastDeepEqual(this._currentReportViewDiffDetails, jsReportViewDiffDetails)
      ) {
        this._currentReportViewDiffDetails = jsReportViewDiffDetails;

        this.loadData();
      }
    };

    _lastSummaryDataOptions: LoadSummaryDataOptions;
    _summaryDataOptionsReaction = (data: LoadSummaryDataOptions) => {
      if (!data) {
        return;
      }

      if (!fastDeepEqual(this._lastSummaryDataOptions, data)) {
        this._lastSummaryDataOptions = klona(data);

        this.loadSummaryData();
      }
    };

    _prevalidateReportViewState = () => {
      let requiresUpdate = false;
      let newReportViewState = this.reportViewState;

      // Validate `sort`
      if (newReportViewState.sort) {
        let removeSort = false;
        if (typeof newReportViewState.sort !== 'object' || newReportViewState.sort === null) {
          console.warn('Report View State property `sort` needs to be an object.');
          removeSort = true;
        } else if (!SORT_DIRECTION_VALUES.includes(newReportViewState.sort.direction)) {
          console.warn(
            "Report View State property `sort.direction` needs to be either 'ASC' or 'DESC'."
          );
          removeSort = true;
        }

        if (removeSort) {
          delete newReportViewState.sort;
          requiresUpdate = true;
        }
      }

      // Validate `layout`
      if (
        newReportViewState.layout &&
        (!this.viewConfig.supportedLayouts ||
          !this.viewConfig.supportedLayouts.includes(newReportViewState.layout))
      ) {
        console.warn(
          `The current Report View does not support the layout of '${newReportViewState.layout}'`
        );
        requiresUpdate = true;

        if (this.viewConfig.supportedLayouts && this.viewConfig.supportedLayouts.length > 0) {
          newReportViewState.layout =
            this.viewConfig.defaultLayout || this.viewConfig.supportedLayouts[0];
        } else {
          delete newReportViewState.layout;
        }
      }

      // Validate `tab`
      if (newReportViewState.tab) {
        if (this.viewConfig.type !== 'TABBED' || Object.keys(this.viewConfig.views).length <= 0) {
          console.warn('The current Report View does not contain any tabs.');
          requiresUpdate = true;
          delete newReportViewState.tab;
        } else if (!Object.keys(this.viewConfig.views).includes(newReportViewState.tab)) {
          console.warn(
            `The current Report View does not include the tab '${newReportViewState.tab}.'`
          );
          requiresUpdate = true;
          newReportViewState.tab =
            this.viewConfig.defaultTab || Object.keys(this.viewConfig.views)[0];
        }
      }

      // Validate `filters`
      if (newReportViewState.filters) {
        newReportViewState.filters = newReportViewState.filters.filter(filter => {
          if (!filter.key) {
            console.warn('All filters need a `key` property.');
            requiresUpdate = true;

            return false;
          }

          if (!filter.values) {
            console.warn('All filters need a `values` property.');
            requiresUpdate = true;

            return false;
          }

          return true;
        });

        // Validate the region name filter
        if (this.viewConfig.type === 'SINGLE_WITH_SUMMARY') {
          const regionFilter = newReportViewState.filters.find(
            filter => filter.key === RegionStore.REGION_FILTER_KEY
          );

          if (regionFilter) {
            const { regionStore } = this.props;

            const optionsInfo = regionStore.regionOptionsInfo;
            const [currentFilterValue] = regionFilter.values;

            if (
              currentFilterValue &&
              !regionStore.regionsLoading &&
              !optionsInfo.options.map(option => option.id).includes(currentFilterValue)
            ) {
              regionFilter.values = [optionsInfo.restricted ? optionsInfo.options[0].id : 'ALL'];

              requiresUpdate = true;
            }
          }
        }
      }

      if (this.onPrevalidateReportViewState) {
        const result = this.onPrevalidateReportViewState(newReportViewState);

        if (result !== true) {
          requiresUpdate = true;
          newReportViewState = result;
        }
      }

      if (requiresUpdate) {
        this.updateReportViewState('replace', newReportViewState);
        return false;
      }

      return true;
    };

    _postValidateReportViewState = () => {
      let requiresUpdate = false;
      let newReportViewState = this.reportViewState;

      // Validate `page`
      if (this.currentViewData && newReportViewState.page > this.currentViewData.meta.lastPage) {
        requiresUpdate = true;
        newReportViewState.page = this.currentViewData.meta.lastPage;
      }

      if (this.onPostvalidateReportViewState) {
        const result = this.onPostvalidateReportViewState(newReportViewState);

        if (result !== true) {
          requiresUpdate = true;
          newReportViewState = result;
        }
      }

      if (requiresUpdate) {
        this.updateReportViewState('replace', newReportViewState);
        return false;
      }

      return true;
    };

    // Factory-point definable methods
    onPrevalidateReportViewState: ?ReportViewStateValidateCallback =
      options && options.onPrevalidateReportViewState;
    onPostvalidateReportViewState: ?ReportViewStateValidateCallback =
      options && options.onPostvalidateReportViewState;

    computeNewUrlFilters = (
      itemsToAdd: SingularConsolidatedFilterItem[],
      itemsToRemove: SingularConsolidatedFilterItem[]
    ): URLFilterItem[] => {
      const newUrlFilters: URLFilterItem[] = this.reportViewState.filters
        ? this.reportViewState.filters.slice()
        : [];

      for (const item: URLFilterItem of itemsToRemove) {
        const targetUrlFilterIndex = newUrlFilters.findIndex(filter => filter.key === item.key);

        const matchingImplicitConsolidatedFilters = this.implicitFilterItems.filter(
          filter => filter.key === item.key
        );
        const matchingImplicitConsolidatedFilterValues = Array.from(
          new Set(
            matchingImplicitConsolidatedFilters
              ? matchingImplicitConsolidatedFilters.flatMap(filter => filter.values)
              : []
          )
        );

        const newFilterValues = [];
        const targetUrlFilterItem: URLFilterItem = newUrlFilters[targetUrlFilterIndex]
          ? { ...newUrlFilters[targetUrlFilterIndex], forceRemove: item.forceRemove || false }
          : false;
        const targetUrlFilter: URLFilterItem = targetUrlFilterItem || {
          key: item.key,
          values: newFilterValues,
        };

        if ('isFixed' in item) {
          targetUrlFilter.fixed = item.fixed;
        }

        if (matchingImplicitConsolidatedFilterValues.length) {
          newFilterValues.push(...matchingImplicitConsolidatedFilterValues);
          targetUrlFilter.overrideImplicit = true;
        }

        targetUrlFilter.values = Array.from(
          new Set([...targetUrlFilter.values, ...newFilterValues])
        ).filter(filterValue => filterValue !== item.value);

        if (
          targetUrlFilter.forceRemove ||
          (!targetUrlFilter.overrideImplicit &&
            !targetUrlFilter.values.length &&
            targetUrlFilterIndex >= 0)
        ) {
          newUrlFilters.splice(targetUrlFilterIndex, 1);
          if (targetUrlFilter.values.length) {
            newUrlFilters.push(targetUrlFilter);
          }
        } else {
          newUrlFilters.splice(targetUrlFilterIndex, 1);
          newUrlFilters.push(targetUrlFilter);
        }
      }

      for (const item of itemsToAdd) {
        const targetUrlFilterIndex = newUrlFilters.findIndex(
          filter => filter.key === item.key && (item.overridesImplicit || !filter.overrideImplicit)
        );

        if (targetUrlFilterIndex >= 0) {
          const targetUrlFilter: URLFilterItem = newUrlFilters[targetUrlFilterIndex];
          targetUrlFilter.values = Array.from(new Set([...targetUrlFilter.values, item.value]));
          if (item.overridesImplicit) {
            targetUrlFilter.overrideImplicit = true;
          }
        } else {
          const targetUrlFilter: URLFilterItem = {
            key: item.key,
            values: [item.value],
          };

          if ('fixed' in item) {
            targetUrlFilter.fixed = item.fixed;
          }

          if (item.overridesImplicit) {
            targetUrlFilter.overrideImplicit = true;
          }

          newUrlFilters.push(targetUrlFilter);
        }
      }

      return newUrlFilters;
    };

    computeSingularFilterDifference = (
      incomingFilterItems: ConsolidatedFilterItem[],
      currentFiltersItems: URLFilterItem[]
    ) => {
      const itemsToAdd: SingularConsolidatedFilterItem[] = incomingFilterItems.flatMap(
        incomingItem => {
          const matchingCurrentFilterItems = currentFiltersItems.filter(
            currentItem =>
              currentItem.key === incomingItem.key || incomingItem.doesNotMatchAvailableFilters
          );

          if (!matchingCurrentFilterItems.length) {
            return incomingItem.values.map(incomingValue => {
              const itemToAdd = {
                ...incomingItem,
                value: incomingValue,
                overridesImplicit: true,
              };

              delete itemToAdd.isDefault;
              delete itemToAdd.userFilterId;
              delete itemToAdd.values;
              return itemToAdd;
            });
          }

          return matchingCurrentFilterItems.flatMap(matchingCurrentItem =>
            incomingItem.values
              .filter(incomingValue => !matchingCurrentItem.values.includes(incomingValue))
              .map(valueToAdd => {
                const itemToAdd = {
                  ...incomingItem,
                  value: valueToAdd,
                };

                delete itemToAdd.isDefault;
                delete itemToAdd.userFilterId;
                delete itemToAdd.values;
                return itemToAdd;
              })
          );
        }
      );

      const itemsToRemove: SingularConsolidatedFilterItem[] = currentFiltersItems.flatMap(
        currentItem => {
          const matchingIncomingItems = incomingFilterItems.filter(
            incomingItem => incomingItem.key === currentItem.key
          );

          if (!matchingIncomingItems.length) {
            return currentItem.values.map(valueToRemove => {
              const itemToRemove: SingularConsolidatedFilterItem = {
                key: currentItem.key,
                value: valueToRemove,
              };

              if ('overrideImplicit' in currentItem) {
                itemToRemove.overridesImplicit = true;
              }

              if ('fixed' in currentItem) {
                itemToRemove.isFixed = currentItem.fixed;
              }

              return itemToRemove;
            });
          }

          return matchingIncomingItems.flatMap(matchingIncomingItem =>
            currentItem.values
              .filter(incomingValue => !matchingIncomingItem.values.includes(incomingValue))
              .map(valueToRemove => {
                const itemToRemove: SingularConsolidatedFilterItem = {
                  key: currentItem.key,
                  value: valueToRemove,
                };

                if ('overrideImplicit' in currentItem) {
                  itemToRemove.overridesImplicit = true;
                }

                if ('fixed' in currentItem) {
                  itemToRemove.isFixed = currentItem.fixed;
                }

                return itemToRemove;
              })
          );
        }
      );

      return {
        itemsToAdd,
        itemsToRemove,
      };
    };

    handlePageChange = (newPage: number) => {
      this.patchReportViewState('push', {
        page: newPage,
      });
    };

    handleTabChange = (e: any, newTab: string) => {
      this.patchReportViewState('push', {
        tab: newTab,
      });
    };

    handleFiltersChange = (newFilters: ConsolidatedFilterItem[] = []) => {
      const { itemsToAdd, itemsToRemove } = this.computeSingularFilterDifference(
        newFilters,
        this.reportViewState.filters || []
      );

      const newUrlFilters = this.computeNewUrlFilters(itemsToAdd, itemsToRemove);

      this.patchReportViewState('replace', {
        filters: newUrlFilters.length ? newUrlFilters : undefined,
      });
    };

    /**
     * Here we only remove the filters that are associated with the selectedUserSearchPreference
     */
    handleUserSearchPreferenceIdChange = (newId?: string) => {
      const filters = this.reportViewState.filters
        ? this.reportViewState.filters.filter((item: URLFilterItem) => {
            const userSearchPreferenceFilters = this.selectedUserSearchPreference
              ? this.selectedUserSearchPreference.searchConfig.filters
              : [];
            return userSearchPreferenceFilters.some((itemToRemove: URLFilterItem) => {
              return itemToRemove.key !== item.key;
            });
          })
        : [];

      this.updateReportViewState('replace', {
        ...this.reportViewState,
        filters: undefined,
        searchTerm: undefined,
      });

      const newReportViewState = {
        ...this.reportViewState,
        filters: filters.length > 0 ? filters : undefined,
        searchTerm: undefined,
      };

      if (!newId) {
        delete newReportViewState.userSearchPreference;
      } else {
        newReportViewState.userSearchPreference = newId;
      }

      this.updateReportViewState('replace', newReportViewState);
    };

    handleDeleteFilterItemChip = (item: SingularConsolidatedFilterItem, activeFilters) => {
      const userPreferenceFilters = this.selectedUserSearchPreference
        ? this.selectedUserSearchPreference.searchConfig.filters
        : [];
      const userPreferenceFilter = userPreferenceFilters.filter(
        (selectedUserPreferenceFilter: UserFilterItem) => {
          return (
            selectedUserPreferenceFilter.key === item.key &&
            selectedUserPreferenceFilter.value === item.value
          );
        }
      );
      const filtersToPersist =
        userPreferenceFilters.length > 1
          ? userPreferenceFilters.filter(
              (persistItem: UserFilterItem) => persistItem.key !== item.key
            )
          : [];
      const userSearchTermsArray = this.selectedUserSearchPreference
        ? this.selectedUserSearchPreference.searchConfig.search
        : [];
      const userSearchTerms = userSearchTermsArray.join(' ') || this.reportViewState.searchTerm;

      const newUrlFilters = this.computeNewUrlFilters(
        [...filtersToPersist],
        [{ ...item, forceRemove: true }]
      );

      const newReportViewState = {
        ...this.reportViewState,
        searchTerm: userSearchTerms && userSearchTerms.length > 0 ? userSearchTerms : undefined,
        filters: newUrlFilters.length ? newUrlFilters : undefined,
      };

      if (userPreferenceFilter.length === 1) {
        newReportViewState.userSearchPreference = undefined;
      }

      if (newReportViewState.filters) {
        const isOnlyAccount =
          newReportViewState.filters.length === 1 &&
          newReportViewState.filters[0].key === TYPEOFFILTERS.ACCOUNT;
        if (item.value === TYPEOFFILTERS.OPEN && isOnlyAccount) {
          const activeAccount = activeFilters.find(filter => filter.key === TYPEOFFILTERS.ACCOUNT);
          newReportViewState.filters = [
            { key: TYPEOFFILTERS.ERSTATUS, overrideImplicit: true, values: [] },
          ];
          newReportViewState.searchTerm = activeAccount
            ? Object.keys(activeAccount.labels)[0]
            : TYPEOFFILTERS.OPEN;
        }
      }

      this.patchReportViewState('replace', newReportViewState);
    };

    handleDeleteSearchTermChip = () => {
      const filtersToPersist = this.selectedUserSearchPreference
        ? this.selectedUserSearchPreference.searchConfig.filters
        : [];
      const newUrlFilters = this.computeNewUrlFilters([...filtersToPersist], []);
      this.patchReportViewState('replace', {
        ...this.reportViewState,
        searchTerm: undefined,
        filters: newUrlFilters.length ? newUrlFilters : undefined,
        userSearchPreference: undefined,
      });
    };

    handleSaveSearchClick = async (name: string, isUpdate?: boolean) => {
      const resultId = await this.props.reportViewStore.saveUserSearchPreference(
        this.viewConfig.userFilterNamespace,
        name,
        {
          filters: this.filters.flatMap(filter =>
            filter.values.map(value => ({ value, key: filter.key }))
          ),
          search: typeof this.searchTerm === 'string' ? [this.searchTerm] : [],
        },
        isUpdate && this.selectedUserSearchPreference
          ? this.selectedUserSearchPreference.id
          : undefined
      );

      this.updateReportViewState('replace', {
        ...this.reportViewState,
        filters: undefined,
        search: undefined,
        userSearchPreference: resultId,
      });
    };

    handleRemoveUserSearchPreference = (userSearchPreference: UserSearchPreference) => {
      this.handleUserSearchPreferenceIdChange();

      this.props.reportViewStore.removeUserSearchPreference(
        userSearchPreference.id,
        this.viewConfig.userFilterNamespace
      );
    };

    async handleDownloadClick(loadingKey: string) {
      console.log('GRID DOWNLOAD CLICKED', {
        loadingKey,
        codename: this.viewConfig.codename,
        dataPathname: this.currentViewConfig.dataPathname,
      });

      const { actionDrawerStore } = this.props;

      actionDrawerStore.setLoadingEntry(loadingKey, true);

      const uri = '/export/csv';
      const dataPathName = this.currentViewConfig.dataPathname;
      const url = this.props.reportViewStore.formulateUrl(
        this.currentViewConfig.apiType,
        `${dataPathName}${uri}`,
        this.loadDataOptions
      );

      var fileName =
        this.viewConfig.codename === SEARCHVIEWCODES.CONTRACTS
          ? SEARCHVIEWCODES.ORDERS
          : this.viewConfig.codename;
      await this.props.actionDrawerStore.getUrlAsCsvDownload(url, fileName);

      actionDrawerStore.setLoadingEntry(loadingKey, false);
    }

    handleActionCompleted = () => {
      this.selection.selectedItems = [];
      this.props.reportViewStore.invalidateCacheByCodename(this.viewConfig.codename);
      this.loadData();
      this.loadSummaryData();
    };

    renderPaginationControls = () => {
      const totalPages =
        (this.lastReasonableViewData && this.lastReasonableViewData.meta.lastPage) || 0;

      return (
        this.lastReasonableViewData && (
          <UltimatePaginationMUI
            totalPages={totalPages}
            currentPage={Math.min(this.reportViewState.page, totalPages)}
            siblingPagesRange={
              this.reportViewState.page < 5 ||
              this.lastReasonableViewData.meta.lastPage - this.reportViewState.page < 4
                ? 1
                : 2
            }
            onChange={this.handlePageChange}
          />
        )
      );
    };

    renderDataArea = () => {
      if (!this.currentViewData || !this.currentViewData.data) {
        return <Loading style={{ height: 'auto' }} />;
      }

      if (!this.currentViewData.data.length) {
        return this.renderNoData();
      }

      if (!this.viewConfig.supportedLayouts || !this.viewConfig.supportedLayouts.length) {
        return this.renderDataTable();
      }

      switch (this.reportViewState.layout) {
        case 'GRID':
          return this.renderGrid();

        case 'TABLE':
        default:
          return this.renderDataTable();
      }
    };

    renderDataTable = () => {
      const drawerActions = this.isCaaSFilterActive
        ? this.currentViewConfig.actionsCaas
        : this.currentViewConfig.actions;
      return (
        <DataTable
          selection={this.selection}
          actions={drawerActions}
          dataStoreEntry={this.currentViewData}
          dataViewConfig={this.currentViewConfig}
          sort={this.sort || undefined}
          onRequestSortChange={sort => {
            this.patchReportViewState('replace', {
              sort,
            });
          }}
          onActionCompleted={this.handleActionCompleted}
          disableSelectAll={this.viewConfig.disabledSelectAll}
        />
      );
    };

    renderGrid = () => {
      const GridItemComponent = this.currentViewConfig && this.currentViewConfig.gridItemComponent;
      if (!GridItemComponent || !this.currentViewData || !this.currentViewData.data) {
        console.error('No Grid Component specified but attempted to render Grid view.');

        return this.renderNoData();
      }

      return (
        <div className={styles['card-grid']}>
          {this.currentViewData.data.map((row, index) => (
            <GridItemComponent key={`grid-item-${index}`} rowData={row} />
          ))}
        </div>
      );
    };

    renderNoData() {
      return (
        <p className={styles['no-results']}>
          {this.props.t('general:search_results.no_results_found')}
        </p>
      );
    }

    renderTabHeaderArea = () => {
      const { t } = this.props;

      if (this.viewConfig.type === 'TABBED') {
        return (
          <ElectroTabs value={this.reportViewState.tab || ''} onChange={this.handleTabChange}>
            {Object.entries(this.viewConfig.views).map(([key, view]) => {
              let tabCount =
                this.lastReasonableViewData &&
                this.lastReasonableViewData.meta.tabCounts &&
                this.lastReasonableViewData.meta.tabCounts[key];

              tabCount = typeof tabCount === 'number' ? `(${tabCount})` : '';

              return (
                <Tab
                  disabled={tabCount === '(0)'}
                  key={key}
                  value={key}
                  label={`${t(view.title)} ${tabCount}`}
                />
              );
            })}
          </ElectroTabs>
        );
      }
    };

    renderActionDrawerSection = () => {
      const { actionDrawerStore } = this.props;
      const selectedItems =
        this.selection.selectedItems.length > 0 ? this.selection.selectedItems : undefined;
      return (
        <ActionDrawerWrapper onActionCompleted={this.handleActionCompleted}>
          {
            {
              CaaSDetails: (
                <CaaSDetails
                  formData={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              CaaSDetailsMultiple: (
                <CaaSDetailsMultiple
                  formData={
                    this.selection.selectedItems.length ? this.selection.selectedItems : undefined
                  }
                />
              ),
              reassignLead: (
                <ReassignProjectLead
                  project={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              extendProject: (
                <ExtendProject
                  project={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              endProject: (
                <EndProject
                  project={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              reassign: <ReassignAsset formData={selectedItems} autoClose={true} />,
              assignAssetToProject: (
                <AllocateAssetToProject formData={selectedItems} autoClose={true} />
              ),
              scheduleCalibration: (
                <ScheduleAssetCalibration
                  formData={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              scheduleAssetCalMulti: (
                <ScheduleAssetCalMulti formData={this.selection.selectedItems} />
              ),
              changeMultipleLocations: (
                <ChangeMultipleAssetsLocations formData={this.selection.selectedItems} />
              ),
              extendProjectMulti: <ExtendProjectMulti project={this.selection.selectedItems} />,
              endProjectMulti: <EndProjectMulti project={this.selection.selectedItems} />,
              requestOwnershipMulti: (
                <RequestOwnershipMulti formData={this.selection.selectedItems} />
              ),
              requestOwnership: (
                <RequestAssetOwnership
                  formData={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              disableAccount: (
                <DisableAccount
                  currentAccountDetails={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              enableAccount: (
                <EnableAccount
                  currentAccountDetails={
                    this.selection.selectedItems.length
                      ? this.selection.selectedItems[0]
                      : undefined
                  }
                />
              ),
              disableAccountMulti: (
                <DisableAccountMulti currentAccountDetails={this.selection.selectedItems} />
              ),
              enableAccountMulti: (
                <EnableAccountMulti currentAccountDetails={this.selection.selectedItems} />
              ),
              assetAuditMulti: <SelfAuditMulti formData={this.selection.selectedItems} />,
              loginAsUser: <LoginAsUser formData={{}} currentAccountDetails={{}} />,
              viewAsUser: <ViewAsUser formData={{}} currentAccountDetails={{}} />,
              deleteEmailDomain: actionDrawerStore.currentActionDrawerData ? (
                <DeleteEmailDomain
                  formData={{
                    id: actionDrawerStore.currentActionDrawerData.emailDomainToDeleteId,
                  }}
                />
              ) : null,
              deleteCustomerAccount: actionDrawerStore.currentActionDrawerData ? (
                <DeleteCustomerAccount
                  formData={{
                    account: actionDrawerStore.currentActionDrawerData.customerAccountToDelete,
                    dataSource: actionDrawerStore.currentActionDrawerData.dataSource,
                  }}
                />
              ) : null,
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      );
    };

    renderPageTitle = () => {
      const { t, location } = this.props;

      const { locationName } = location.state || {};
      const title = locationName
        ? `${t('general:asset_audit')} ${locationName} - ${t(this.viewConfig.title)}`
        : t(this.viewConfig.title);

      return (
        <PageTitle
          title={{ text: title, count: this.pageTotalCount }}
          actionButtons={this.viewConfig.buttons}
          titleSlot={<RefreshIndicator state={this.refreshState} />}
        />
      );
    };

    handleOnSummaryHeaderClick = () => {
      const newValue = !this.reportViewState.summary || !this.reportViewState.summary.expanded;

      this.patchReportViewState('replace', {
        summary: {
          expanded: newValue,
        },
      });
    };

    handleRegionSelect = (newValue: string) => {
      let newFilters = [...(this.reportViewState.filters || [])];
      const regionFilterItemIndex = newFilters.findIndex(
        filterItem => filterItem.key === RegionStore.REGION_FILTER_KEY
      );
      const newFilterItem: URLFilterItem =
        regionFilterItemIndex > -1
          ? klona(newFilters[regionFilterItemIndex])
          : { key: RegionStore.REGION_FILTER_KEY, values: [] };

      newFilterItem.overrideImplicit = true;
      newFilterItem.fixed = true;
      newFilterItem.values = [newValue];

      if (regionFilterItemIndex > -1) {
        newFilters.splice(regionFilterItemIndex, 1, newFilterItem);
      } else {
        newFilters.push(newFilterItem);
      }

      this.patchReportViewState('replace', {
        filters: newFilters,
      });
    };

    renderSummarySection = () => {
      if (this.viewConfig.type === 'SINGLE_WITH_SUMMARY') {
        const { reportViewStore } = this.props;
        const { summaryView } = this.viewConfig;

        const regionFilter = this.filters.find(
          filter => filter.key === RegionStore.REGION_FILTER_KEY
        );
        const regionVisible = summaryView && summaryView.enableRegionSelect;
        const summaryLoading = !this.currentViewSummaryData || this.currentViewSummaryData.loading;

        const value = regionFilter ? regionFilter.values[0] : undefined;

        const summaryButtons = (this.viewConfig.summaryView.sectionHeadingButtons || []).map(
          button => ({
            ...button,
            onClick: event => {
              if (event && event.preventDefault) {
                event.preventDefault();
              }

              if (event && event.stopPropagation) {
                event.stopPropagation();
              }

              const result = button.onClick ? button.onClick(event) : undefined;

              return Promise.resolve(result).finally(() => {
                this.props.reportViewStore.loading.exportSummary = false;

                this.props.actionDrawerStore.setLoadingEntry(this.viewConfig.codename, false);
                this.props.actionDrawerStore.setLoadingEntry(
                  `${this.viewConfig.codename}-download`,
                  false
                );
                this.props.actionDrawerStore.setLoadingEntry(
                  `download-${this.viewConfig.codename}`,
                  false
                );
              });
            },
          })
        );

        return (
          <React.Fragment>
            <PageTitle
              title={this.viewConfig.summaryView.title}
              logoPath={this.viewConfig.summaryView.logoPath}
              actionButtons={this.viewConfig.summaryView.buttons}
              titleSlot={
                regionVisible && <RegionSelect value={value} onSelect={this.handleRegionSelect} />
              }
            />
            <ReportSummary
              data={this.currentViewSummaryLayout}
              loading={summaryLoading}
              title={this.viewConfig.summaryView.sectionHeading}
              expanded={this.reportViewState.summary && this.reportViewState.summary.expanded}
              onExportDataClick={() => {}}
              onHeaderClick={this.handleOnSummaryHeaderClick}
              buttons={summaryLoading ? [] : summaryButtons}
              buttonsLoading={summaryLoading || reportViewStore.loading.exportSummary}
            />
          </React.Fragment>
        );
      }

      return null;
    };

    /**
     * Renders the toggle button to apply CaaS Filter
     */
    renderCaaSToggle() {
      let disableCaaSToggle = this.refreshState !== 'up-to-date';
      const activeUser: Object = JSON.parse(localStorage.getItem('user'));
      // Disable CaaS toggle for BUSINESS_VIEW_ONLY and VIEW_ONLY roles
      if (
        activeUser.role_id === Roles.BUSINESS_VIEW_ONLY ||
        activeUser.role_id === Roles.VIEW_ONLY
      ) {
        disableCaaSToggle = true;
      }

      return (
        <div className={`${electroSwitchStyles['electro-switch']}`}>
          <b>{this.props.t('general:assets.all')}</b>
          <Switch
            color="primary"
            checked={this.isCaaSFilterActive}
            onChange={this.handleCaaSToggleOnChange}
            disabled={disableCaaSToggle}
          />
          <b>{this.props.t('general:assets.caas')}</b>
        </div>
      );
    }

    /**
     * Get the current applied filters and then add/remove the is_caas_asset
     * filter based on whether the toggle button is active or not, and
     * finally apply the filters by calling this.handleFiltersChange()
     */
    handleCaaSToggleOnChange = (e: SyntheticEvent<HTMLInputElement>) => {
      let newFilters = this.filters.filter(f => f.key !== 'is_caas_asset');

      if (e.target.checked) {
        newFilters.push({
          type: 'string',
          values: ['Yes'],
          key: 'is_caas_asset',
          name: 'Is Caas Asset',
          overridesImplicit: true,
        });
      }

      this.handleFiltersChange(newFilters);
      this.selection.selectedItems = [];
    };

    render() {
      const {
        t,
        reportViewStore: { loading },
      } = this.props;

      return (
        <React.Fragment>
          {!this.viewConfig.disableFilters && (
            <PageFilters
              filters={this.filters}
              isActive={this.filterDrawerOpen}
              availableFilters={this.currentViewAvailableFilters}
              onChangeFilters={this.handleFiltersChange}
              onChangeUserSearchPreferenceId={this.handleUserSearchPreferenceIdChange}
              onRemoveUserSearchPreference={this.handleRemoveUserSearchPreference}
              selectedUserSearchPreference={this.selectedUserSearchPreference}
              onRequestDrawerClose={() => {
                this.filterDrawerOpen = false;
              }}
              userSearchPreferences={this.userSearchPreferences}
              loadingFilters={!!this.currentViewData && this.currentViewData.filtersLoading}
              tableCodeName={this.viewConfig.codename}
            />
          )}

          <PageWrapper isFilters={this.filterDrawerOpen} isFlex>
            {this.renderSummarySection()}

            {this.renderPageTitle()}
            <PageSearch
              viewConfig={this.viewConfig}
              filters={this.filters}
              layout={this.reportViewState.layout}
              count={this.pageTotalCount}
              searchTerm={this.searchTerm}
              userSearchPreference={this.selectedUserSearchPreference || undefined}
              onFilterButtonClick={() => {
                this.filterDrawerOpen = !this.filterDrawerOpen;
              }}
              onLayoutChangeClick={newLayout => {
                this.patchReportViewState('push', { layout: newLayout });
              }}
              onSearchSubmit={newSearchTerm => {
                this.patchReportViewState('replace', {
                  searchTerm: newSearchTerm,
                });
              }}
              filterDrawerOpen={this.filterDrawerOpen}
              onDownloadClick={key => this.handleDownloadClick(key)}
              onSaveSearchClick={this.handleSaveSearchClick}
              onClearSearch={this.handleDeleteSearchTermChip}
              onDeleteFilterItem={this.handleDeleteFilterItemChip}
              filtersError={
                this.reportViewState.lengthWarning
                  ? {
                      level: 'warning',
                      message: t('general:report_view.url_length_warning_message'),
                    }
                  : undefined
              }
              userSearchPreferenceLoading={loading.saveUserSearchPreference}
            />
            {this.isCaaSFilterAvailable ? this.renderCaaSToggle() : null}
            <div className={styles['pagination-top']}>{this.renderPaginationControls()}</div>

            {this.renderTabHeaderArea()}
            {this.renderDataArea()}

            <div className={styles['pagination-bottom']}>{this.renderPaginationControls()}</div>
          </PageWrapper>

          {this.renderActionDrawerSection()}
        </React.Fragment>
      );
    }
  }

  return ReportViewPageBase;
}
